// Minimal state-store layer (no external library) — the "build a custom
// state-store" item from the original refactor spec.
//
// Scope decision, stated plainly: this does NOT replace public/index.html's
// `let ORDERS/PRODUCTS/SETTINGS` bindings with store-owned state. Doing
// that properly means every one of the several hundred existing read/mutate
// call sites across the inline script would need to change from a bare
// `ORDERS` reference to something like `store.getOrders()` — a rewrite of
// that size, with no browser available in this environment to verify each
// call site still behaves correctly, is exactly the kind of blind, high-risk
// change the rest of this refactor has deliberately avoided (see
// REFACTOR_SECURITY_REPORT.md's Phase 1/2 sections for the same reasoning
// applied elsewhere).
//
// What this DOES provide, safely: a real subscribe/notify primitive, wired
// into the small number of places state is already reassigned wholesale —
// `saveAll()`, `window.ML_CRM.importState()`, and `window.ML_CRM.clearState()`
// in public/index.html — rather than the hundreds of places it's read.
// That's a strict addition (zero current subscribers = zero behavior
// change) that gives Phase 4's routing work, or any future reactive
// re-rendering, one real place to hook into instead of needing its own ad
// hoc polling or a blind full-state rewrite first.
const listeners = new Set();

// Returns an unsubscribe function, the standard convention used elsewhere
// in this codebase (see firebase-client.js's onSnapshot unsubscribers).
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// A listener throwing must never break the caller that triggered the
// notification (that caller is usually mid-save) — isolate each listener's
// errors the same way logActivity() isolates audit-log failures from the
// action they're describing (src/firebase-client.js).
export function notifyStateChanged(snapshot) {
  listeners.forEach(listener => {
    try { listener(snapshot); } catch (error) { console.error('store listener failed', error); }
  });
}

export function listenerCount() {
  return listeners.size;
}
